<template>
  <v-navigation-drawer
    v-model="show"
    right
    temporary
    fixed
    :width="$vuetify.breakpoint.smAndDown ? $vuetify.breakpoint.width : 760"
    class="pdrawer"
  >
    <div v-if="property" class="pdrawer__wrap">
      <!-- top bar: the ONLY fixed piece (close + address + quick actions) -->
      <div class="pdrawer__bar">
        <button type="button" class="pdrawer__close" @click="show = false" title="Cerrar">
          <hero-icon :icon="XMarkIcon" :size="18" />
        </button>
        <div class="pdrawer__baraddr">{{ address }}</div>
        <div class="pdrawer__baractions">
          <button type="button" class="pdrawer__chip" @click="copyLink" title="Copiar enlace">
            <hero-icon :icon="LinkIcon" :size="15" />
          </button>
          <button type="button" class="pdrawer__chip" @click="openWeb" title="Ver en la web">
            <hero-icon :icon="ExternalIcon" :size="15" />
          </button>
        </div>
      </div>

      <!-- everything below scrolls; the tabs re-pin at the top once reached -->
      <div class="pdrawer__scroll">
        <div class="pdrawer__header">
          <div class="pdrawer__type">{{ typeLabel }}</div>
          <div class="pdrawer__badges">
            <span class="pbadge" :class="'pbadge--' + property.state.toLowerCase()">
              {{ stateLabel }}
            </span>
            <span v-for="op in property.operationTypes" :key="op" class="pdrawer__op">
              {{ opLabel(op) }}
            </span>
            <span class="pdrawer__nro">N.º {{ property.id }}</span>
          </div>
          <div class="pdrawer__actions">
            <button type="button" class="pdrawer__action" @click="edit">
              <hero-icon :icon="PencilIcon" :size="14" /> Editar
            </button>
            <button type="button" class="pdrawer__action" @click="disable">
              {{ property.disabled ? "Habilitar" : "Deshabilitar" }}
            </button>
            <button
              v-if="['published', 'booked'].includes(property.state)"
              type="button"
              class="pdrawer__action"
              @click="book"
            >
              {{ property.state === "booked" ? "Quitar reserva" : "Reservar" }}
            </button>
            <button type="button" class="pdrawer__action" @click="feature">
              {{ property.featured ? "Quitar destacado" : "Destacar" }}
            </button>
          </div>
        </div>

        <v-tabs
          v-model="tab"
          color="primary"
          slider-color="accent"
          background-color="transparent"
          class="pdrawer__tabs"
        >
          <v-tab>Detalle</v-tab>
          <v-tab :disabled="!hasContracts">Contrato</v-tab>
          <v-tab>Impuestos</v-tab>
          <v-tab>Reparaciones</v-tab>
        </v-tabs>

        <div class="pdrawer__body">
        <!-- ============ DETALLE ============ -->
        <div v-show="tab === 0">
          <!-- carousel -->
          <div class="pdrawer__gallery">
            <div class="pdrawer__main">
              <img
                v-if="images.length"
                :src="img(images[activeImage], 'smallUrl')"
                alt=""
                @click="lightbox = true"
              />
              <div v-else class="pdrawer__noimg"><hero-icon :icon="HomeIcon" :size="40" /></div>
              <span v-if="!isVisible" class="pdrawer__flag">No visible en la web</span>
              <template v-if="images.length > 1">
                <button type="button" class="pdrawer__nav pdrawer__nav--prev" @click.stop="step(-1)">
                  <hero-icon :icon="ChevronLeftIcon" :size="20" />
                </button>
                <button type="button" class="pdrawer__nav pdrawer__nav--next" @click.stop="step(1)">
                  <hero-icon :icon="ChevronRightIcon" :size="20" />
                </button>
                <span class="pdrawer__counter">{{ activeImage + 1 }} / {{ images.length }}</span>
              </template>
            </div>
            <div v-if="images.length > 1" class="pdrawer__thumbs">
              <button
                v-for="(im, i) in images"
                :key="im.id"
                ref="thumbs"
                type="button"
                class="pdrawer__thumb"
                :class="{ 'pdrawer__thumb--on': i === activeImage }"
                @click="activeImage = i"
              >
                <img :src="img(im, 'thumbUrl')" alt="" />
              </button>
            </div>
          </div>

          <!-- prices -->
          <div v-if="prices.length" class="pdrawer__prices">
            <div v-for="p in prices" :key="p.label" class="pdrawer__price">
              <span class="pdrawer__price-label">{{ p.label }}</span>
              <span class="pdrawer__price-value">{{ p.value }}</span>
            </div>
          </div>

          <!-- performance (public-site analytics) -->
          <div v-if="full" class="pdrawer__section">
            <div class="pdrawer__label pdrawer__label--row">
              <span>Rendimiento</span>
              <span class="pdrawer__perf-hint">Ver detalle →</span>
            </div>
            <analytics-dialog ref="analytics" :property-id="property.id" :property-label="address" />
            <inquiries-dialog ref="inquiriesDialog" />
            <div class="pdrawer__perf">
              <div class="pdrawer__perf-tile" @click="$refs.analytics.open()">
                <div class="pdrawer__perf-icon">
                  <hero-icon :icon="EyeIcon" :size="18" />
                </div>
                <div>
                  <div class="pdrawer__perf-num">{{ property.visitsCount || 0 }}</div>
                  <div class="pdrawer__perf-lbl">Visitas</div>
                </div>
              </div>
              <div class="pdrawer__perf-tile" @click="$refs.analytics.open()">
                <div class="pdrawer__perf-icon">
                  <hero-icon :icon="MagnifyingGlassIcon" :size="18" />
                </div>
                <div>
                  <div class="pdrawer__perf-num">{{ property.searchAppearancesCount || 0 }}</div>
                  <div class="pdrawer__perf-lbl">En búsquedas</div>
                </div>
              </div>
              <div class="pdrawer__perf-tile" @click="$refs.inquiriesDialog.open(property)">
                <div class="pdrawer__perf-icon">
                  <hero-icon :icon="ChatBubbleIcon" :size="18" />
                </div>
                <div>
                  <div class="pdrawer__perf-num">{{ property.inquiriesCount || 0 }}</div>
                  <div class="pdrawer__perf-lbl">Consultas</div>
                </div>
              </div>
            </div>
          </div>

          <!-- headline specs -->
          <div v-if="specs.length" class="pdrawer__section">
            <div class="pdrawer__label">Características</div>
            <div class="pdrawer__specs">
              <div v-for="s in specs" :key="s.label" class="pdrawer__spec">
                <v-icon size="19">{{ s.icon }}</v-icon>
                <span class="pdrawer__spec-val">{{ s.value }}</span>
                <span class="pdrawer__spec-label">{{ s.label }}</span>
              </div>
            </div>
          </div>

          <!-- other numeric details -->
          <div v-if="details.length" class="pdrawer__section">
            <div class="pdrawer__label">Detalles</div>
            <div class="pdrawer__details">
              <div v-for="d in details" :key="d.name" class="pdrawer__detail">
                <span class="pdrawer__detail-k">{{ d.name }}</span>
                <span class="pdrawer__detail-v">{{ d.value }}</span>
              </div>
            </div>
          </div>

          <!-- rental conditions -->
          <div v-if="rentalConditions.length" class="pdrawer__section">
            <div class="pdrawer__label">Condiciones de alquiler</div>
            <div class="pdrawer__details">
              <div v-for="d in rentalConditions" :key="d.name" class="pdrawer__detail">
                <span class="pdrawer__detail-k">{{ d.name }}</span>
                <span class="pdrawer__detail-v">{{ d.value }}</span>
              </div>
            </div>
          </div>

          <!-- amenities -->
          <div v-if="amenities.length" class="pdrawer__section">
            <div class="pdrawer__label">Comodidades y servicios</div>
            <div v-for="group in amenities" :key="group.name" class="pdrawer__amen">
              <div class="pdrawer__amen-name">{{ group.name }}</div>
              <div class="pdrawer__amen-chips">
                <span v-for="(v, i) in group.values" :key="i" class="pdrawer__amen-chip">{{ v }}</span>
              </div>
            </div>
          </div>

          <!-- blueprint -->
          <div v-if="property.blueprintUrl" class="pdrawer__section">
            <div class="pdrawer__label">Plano</div>
            <img
              class="pdrawer__blueprint"
              :src="url + property.blueprintUrl"
              alt=""
              @click="openImage(url + property.blueprintUrl)"
            />
          </div>

          <!-- description -->
          <div v-if="property.description" class="pdrawer__section">
            <div class="pdrawer__label">Descripción</div>
            <p class="pdrawer__desc">{{ stripHtml(property.description) }}</p>
          </div>

          <!-- owner -->
          <div v-if="owners.length" class="pdrawer__section">
            <div class="pdrawer__label">{{ owners.length > 1 ? "Propietarios" : "Propietario" }}</div>
            <div v-for="o in owners" :key="o.id" class="pdrawer__owner">
              <span class="pdrawer__owner-name">{{ ownerName(o) }}</span>
              <a v-if="o.email" class="pdrawer__owner-mail" :href="'mailto:' + o.email">{{ o.email }}</a>
            </div>
          </div>

          <!-- asesores -->
          <div v-if="full" class="pdrawer__section">
            <div class="pdrawer__label">Asesores</div>
            <div v-if="full.staff && full.staff.length" class="pdrawer__advisors">
              <span v-for="s in full.staff" :key="s.id" class="pdrawer__advisor">
                {{ s.name }}
                <button type="button" class="pdrawer__advisor-x" @click="detachStaff(s.id)">
                  <hero-icon :icon="XMarkIcon" :size="13" />
                </button>
              </span>
            </div>
            <div class="pdrawer__assign">
              <v-select
                v-model="employee"
                :items="staff"
                item-value="id"
                item-text="name"
                outlined
                dense
                hide-details
                label="Asignar asesor…"
                class="pdrawer__assign-select"
              ></v-select>
              <v-btn
                :disabled="!employee"
                color="primary"
                depressed
                class="rounded-lg"
                @click="attachStaff"
                >Asignar</v-btn
              >
            </div>
          </div>

          <!-- observaciones -->
          <div v-if="full" class="pdrawer__section">
            <div class="pdrawer__obs-head">
              <div class="pdrawer__label" style="margin: 0">
                Observaciones <span class="pdrawer__count">{{ observations.length }}</span>
              </div>
              <observations
                :observations="observations"
                :property-id="property.id"
                @reload="fetchObservations"
              ></observations>
            </div>
          </div>
        </div>

        <!-- ============ CONTRATO ============ -->
        <div v-if="tab === 1" class="pdrawer__reuse">
          <show-contract v-if="full" :property="full"></show-contract>
          <div v-else class="pdrawer__loading">Cargando…</div>
        </div>

        <!-- ============ IMPUESTOS ============ -->
        <div v-if="tab === 2" class="pdrawer__reuse">
          <taxes v-if="full" :property="full" @reload="refetch"></taxes>
          <div v-else class="pdrawer__loading">Cargando…</div>
        </div>

        <!-- ============ REPARACIONES ============ -->
        <div v-if="tab === 3" class="pdrawer__reuse">
          <fixes v-if="full" :property="full"></fixes>
          <div v-else class="pdrawer__loading">Cargando…</div>
        </div>
        </div>
      </div>
    </div>

    <!-- lightbox -->
    <v-dialog v-model="lightbox" fullscreen>
      <div class="pdlight" @click="lightbox = false">
        <button type="button" class="pdlight__close" @click.stop="lightbox = false">
          <hero-icon :icon="XMarkIcon" :size="22" />
        </button>
        <button v-if="images.length > 1" type="button" class="pdlight__nav pdlight__nav--prev" @click.stop="step(-1)">
          <hero-icon :icon="ChevronLeftIcon" :size="28" />
        </button>
        <img v-if="images.length" :src="img(images[activeImage], 'url')" alt="" @click.stop />
        <button v-if="images.length > 1" type="button" class="pdlight__nav pdlight__nav--next" @click.stop="step(1)">
          <hero-icon :icon="ChevronRightIcon" :size="28" />
        </button>
      </div>
    </v-dialog>

    <v-snackbar v-model="copied" color="success" :timeout="2000">
      Enlace copiado al portapapeles.
    </v-snackbar>
  </v-navigation-drawer>
</template>

<script>
import { mapState } from "vuex";
import { Property } from "@/graphql/queries/property";
import { Observations as ObservationsQuery } from "@/graphql/queries/properties";
import { Staff } from "@/graphql/queries/staff";
import { EditProperty } from "@/graphql/mutations/properties";
import { AttachStaff, DetachStaff } from "@/graphql/mutations/staff";

import HeroIcon from "@/components/shared/HeroIcon";
import ShowContract from "@/components/properties/ShowContract";
import AnalyticsDialog from "@/components/properties/AnalyticsDialog";
import InquiriesDialog from "@/components/properties/InquiriesDialog";
import Taxes from "@/components/properties/Taxes";
import Fixes from "@/components/properties/Fixes";
import Observations from "@/components/properties/observations/Observations";

import XMarkIcon from "heroicons/24/outline/x-mark.svg";
import LinkIcon from "heroicons/24/outline/link.svg";
import ExternalIcon from "heroicons/24/outline/arrow-top-right-on-square.svg";
import HomeIcon from "heroicons/24/outline/home.svg";
import PencilIcon from "heroicons/24/outline/pencil-square.svg";
import ChevronLeftIcon from "heroicons/24/outline/chevron-left.svg";
import ChevronRightIcon from "heroicons/24/outline/chevron-right.svg";
import EyeIcon from "heroicons/24/outline/eye.svg";
import MagnifyingGlassIcon from "heroicons/24/outline/magnifying-glass.svg";
import ChatBubbleIcon from "heroicons/24/outline/chat-bubble-left-right.svg";

const OP_LABELS = { sell: "Venta", rent: "Alquiler" };
const HEADLINE = {
  Dormitorios: { icon: "mdi-bed-king-outline", label: "Dorm." },
  "Baños": { icon: "mdi-shower", label: "Baños" },
  Ambientes: { icon: "mdi-floor-plan", label: "Amb." },
  "Superficie total": { icon: "mdi-arrow-expand", label: "Sup.", suffix: " m²" }
};

export default {
  name: "PropertyDetailDrawer",

  components: { HeroIcon, ShowContract, Taxes, Fixes, Observations, AnalyticsDialog, InquiriesDialog },

  data: () => ({
    show: false,
    summary: null,
    full: null,
    tab: 0,
    activeImage: 0,
    lightbox: false,
    copied: false,
    staff: [],
    observations: [],
    employee: null,
    XMarkIcon,
    LinkIcon,
    ExternalIcon,
    HomeIcon,
    PencilIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    EyeIcon,
    MagnifyingGlassIcon,
    ChatBubbleIcon
  }),

  computed: {
    ...mapState(["url"]),

    property() {
      return this.full || this.summary;
    },

    hasContracts() {
      return !!(this.full && this.full.contracts && this.full.contracts.length);
    },

    images() {
      return (this.property && this.property.images) || [];
    },

    owners() {
      const o = this.property && this.property.owner;
      return Array.isArray(o) ? o : o ? [o] : [];
    },

    address() {
      const a = (this.property && this.property.address) || {};
      const line = [a.street, a.number, a.unit].filter(Boolean).join(" ");
      return [line, a.city, a.state].filter(Boolean).join(", ");
    },

    typeLabel() {
      const t = this.property && this.property.propertyType;
      const type = t ? this.$t("property.type." + t.toLowerCase()) : "";
      const nbhd = this.property && this.property.address && this.property.address.neighborhood;
      return [type, nbhd].filter(Boolean).join(" · ");
    },

    stateLabel() {
      return this.$t("property.state." + this.property.state.toLowerCase());
    },

    isVisible() {
      return (
        !this.property.disabled &&
        ["published", "booked"].includes(this.property.state)
      );
    },

    prices() {
      const p = this.property;
      const out = [];
      (p.operationTypes || []).forEach(op => {
        if (op === "rent" && p.price && p.price !== "$0,00" && p.price !== "$ 0")
          out.push({ label: "Alquiler", value: p.price });
        if (op === "sell" && p.sellPrice && p.sellPrice !== "$0,00" && p.sellPrice !== " 0")
          out.push({ label: "Venta", value: p.sellPrice });
      });
      return out;
    },

    filledChars() {
      return ((this.property && this.property.characteristics) || []).filter(
        c => c.value && c.value !== "0" && String(c.value).trim() !== ""
      );
    },

    specs() {
      return this.filledChars
        .filter(c => HEADLINE[c.name])
        .map(c => ({
          icon: HEADLINE[c.name].icon,
          label: HEADLINE[c.name].label,
          value: c.value + (HEADLINE[c.name].suffix || "")
        }));
    },

    details() {
      return this.filledChars
        .filter(c => !HEADLINE[c.name])
        .map(c => ({ name: c.name, value: c.value }));
    },

    rentalConditions() {
      const f = (this.property && this.property.features) || {};
      if (!(f.operation_type || {}).rent) return [];
      const PETS = { yes: "Sí", no: "No", ask: "Consultar" };
      const out = [];
      if (PETS[f.pets]) out.push({ name: "Admite mascotas", value: PETS[f.pets] });
      if (f.max_occupants) out.push({ name: "Máx. ocupantes", value: f.max_occupants });
      return out;
    },

    amenities() {
      const f = (this.property && this.property.features) || {};
      return (f.additional_features || []).filter(g => g && g.values && g.values.length);
    }
  },

  methods: {
    open(property) {
      this.summary = property;
      this.full = null;
      this.tab = 0;
      this.activeImage = 0;
      this.staff = [];
      this.observations = [];
      this.employee = null;
      this.show = true;
      this.fetchFull(property.id);
    },

    refetch() {
      if (this.property) this.fetchFull(this.property.id);
    },

    fetchFull(id) {
      this.$apollo
        .query({ query: Property, variables: { id }, fetchPolicy: "no-cache" })
        .then(res => {
          this.full = res.data.property;
          this.fetchStaff();
          this.fetchObservations();
        });
    },

    fetchStaff() {
      this.$apollo
        .query({ query: Staff, fetchPolicy: "cache-first" })
        .then(res => {
          const assigned = ((this.full && this.full.staff) || []).map(s => s.id);
          this.staff = res.data.staff.filter(s => !assigned.includes(s.id));
        });
    },

    fetchObservations() {
      this.$apollo
        .query({
          query: ObservationsQuery,
          variables: { id: this.property.id },
          fetchPolicy: "no-cache"
        })
        .then(res => {
          this.observations = res.data.observations;
        });
    },

    mutateProperty(attributes) {
      this.$apollo
        .mutate({
          mutation: EditProperty,
          variables: { input: { propertyId: this.property.id, attributes } }
        })
        .then(() => {
          this.fetchFull(this.property.id);
          this.$emit("reload");
        });
    },

    disable() {
      this.mutateProperty({ disabled: !this.property.disabled });
    },

    book() {
      this.mutateProperty({
        state: this.property.state === "booked" ? "published" : "booked"
      });
    },

    feature() {
      this.mutateProperty({ featured: !this.property.featured });
    },

    attachStaff() {
      if (!this.employee) return;
      this.$apollo
        .mutate({
          mutation: AttachStaff,
          variables: { input: { id: this.employee, propertyId: this.property.id } }
        })
        .then(() => {
          this.employee = null;
          this.fetchFull(this.property.id);
        });
    },

    detachStaff(id) {
      this.$apollo
        .mutate({
          mutation: DetachStaff,
          variables: { input: { id, propertyId: this.property.id } }
        })
        .then(() => this.fetchFull(this.property.id));
    },

    step(dir) {
      const n = this.images.length;
      if (!n) return;
      this.activeImage = (this.activeImage + dir + n) % n;
      this.$nextTick(() => {
        const el = this.$refs.thumbs && this.$refs.thumbs[this.activeImage];
        if (el && el.scrollIntoView)
          el.scrollIntoView({ inline: "center", block: "nearest", behavior: "smooth" });
      });
    },

    img(image, key) {
      const path = image[key] || image.url;
      return path ? this.url + path : "";
    },

    openImage(src) {
      window.open(src, "_blank");
    },

    stripHtml(s) {
      return (s || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    },

    opLabel(op) {
      return OP_LABELS[op] || op;
    },

    ownerName(o) {
      return (o.profile && o.profile.name) || o.email;
    },

    edit() {
      this.$router.push({ name: "EditProperty", params: { id: this.property.id } });
    },

    openWeb() {
      window.open("https://sartorinmobiliaria.com/p/" + this.property.id);
    },

    copyLink() {
      navigator.clipboard.writeText(
        "https://sartorinmobiliaria.com/p/" + this.property.id
      );
      this.copied = true;
    }
  }
};
</script>

<style scoped>
.pdrawer >>> .v-navigation-drawer__content { background: #f7f5f1; overflow: hidden; }

.pdrawer__wrap { display: flex; flex-direction: column; height: 100%; }

/* bar — the only fixed chrome: close + address + quick actions */
.pdrawer__bar {
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid #ece7de;
  background: #fbfaf7;
}
.pdrawer__close {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: none;
  border: none;
  cursor: pointer;
  color: #1a1814;
  transition: background 0.15s ease;
}
.pdrawer__close:hover { background: #f1ece3; }
.pdrawer__baraddr {
  flex: 1;
  min-width: 0;
  font-size: 14.5px;
  font-weight: 700;
  color: #1a1814;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pdrawer__baractions { flex: none; display: flex; gap: 8px; }
.pdrawer__chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  background: #fff;
  border: 1px solid #e3ddd2;
  border-radius: 999px;
  cursor: pointer;
  color: #6a645a;
  transition: background 0.15s ease, color 0.15s ease;
}
.pdrawer__chip:hover { background: #f1ece3; color: #1a1814; }

/* header (scrolls away) */
.pdrawer__header { padding: 16px 22px 0; }
.pdrawer__type {
  font-size: 10.5px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #a39a8c;
  font-weight: 600;
}
.pdrawer__badges { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 9px; }
.pdrawer__op {
  font-size: 11.5px;
  font-weight: 600;
  color: #1a1814;
  background: #ece7de;
  border-radius: 999px;
  padding: 3px 11px;
}
.pdrawer__nro { font-size: 12px; color: #a39a8c; }

.pbadge { font-size: 11px; font-weight: 600; padding: 2px 10px; border-radius: 999px; line-height: 1.6; }
.pbadge--published { background: #e3f3e8; color: #2c6e49; }
.pbadge--booked { background: #fdeecf; color: #8a5a00; }
.pbadge--rented { background: #e1ecff; color: #2353b8; }
.pbadge--draft { background: #ece9e3; color: #6a645a; }

.pdrawer__actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 13px; }
.pdrawer__action {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #fff;
  border: 1px solid #e3ddd2;
  border-radius: 8px;
  cursor: pointer;
  color: #1a1814;
  font-size: 12.5px;
  font-weight: 600;
  padding: 7px 12px;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.pdrawer__action:hover { border-color: #cfc7b8; background: #faf8f4; }
.pdrawer__action .hero-icon { color: #6a645a; }

/* tabs — full-width, re-pin to the top of the scroll once reached */
.pdrawer__tabs {
  position: sticky;
  top: 0;
  z-index: 2;
  margin-top: 14px;
  padding: 0 22px;
  background: #f7f5f1;
  border-bottom: 1px solid #ece7de;
}
.pdrawer__tabs >>> .v-tabs-bar { background: transparent; }
.pdrawer__tabs >>> .v-tab {
  text-transform: none;
  letter-spacing: normal;
  font-size: 14px;
  font-weight: 500;
  min-width: 0;
}
/* drop the reserved arrow gutters + the auto-center transform; scroll natively */
.pdrawer__tabs >>> .v-slide-group__prev,
.pdrawer__tabs >>> .v-slide-group__next { display: none !important; }
.pdrawer__tabs >>> .v-slide-group__wrapper { overflow-x: auto; }
.pdrawer__tabs >>> .v-slide-group__content { transform: none !important; }
.pdrawer__tabs >>> .v-slide-group__wrapper::-webkit-scrollbar { display: none; }

/* scroll (no horizontal padding so the sticky tabs span full width) */
.pdrawer__scroll { flex: 1; min-height: 0; overflow-y: auto; }
.pdrawer__body { padding: 16px 22px 28px; }
.pdrawer__loading { padding: 40px; text-align: center; color: #a39a8c; }
.pdrawer__reuse { margin: 0 -14px; }

/* carousel */
.pdrawer__main {
  position: relative;
  height: 300px;
  border-radius: 14px;
  overflow: hidden;
  background: #ece7de;
}
.pdrawer__main img { width: 100%; height: 100%; object-fit: cover; display: block; cursor: zoom-in; }
.pdrawer__noimg { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #c4bcae; }
.pdrawer__flag {
  position: absolute; top: 12px; left: 12px;
  font-size: 11px; font-weight: 600; color: #fff;
  background: rgba(192, 57, 43, 0.92); border-radius: 999px; padding: 3px 11px;
}
.pdrawer__nav {
  position: absolute; top: 50%; transform: translateY(-50%);
  width: 38px; height: 38px; border: none; border-radius: 50%;
  background: rgba(26, 24, 20, 0.5); color: #fff; cursor: pointer;
  display: flex; align-items: center; justify-content: center; transition: background 0.15s ease;
}
.pdrawer__nav:hover { background: rgba(26, 24, 20, 0.78); }
.pdrawer__nav--prev { left: 12px; }
.pdrawer__nav--next { right: 12px; }
.pdrawer__counter {
  position: absolute; bottom: 12px; right: 12px;
  font-size: 11.5px; font-weight: 600; color: #fff;
  background: rgba(26, 24, 20, 0.6); border-radius: 999px; padding: 3px 10px;
}
.pdrawer__thumbs { display: flex; gap: 8px; margin-top: 8px; overflow-x: auto; padding-bottom: 4px; }
.pdrawer__thumbs::-webkit-scrollbar { height: 6px; }
.pdrawer__thumbs::-webkit-scrollbar-thumb { background: #dcd4c6; border-radius: 999px; }
.pdrawer__thumbs::-webkit-scrollbar-track { background: transparent; }
.pdrawer__thumb {
  flex: none; width: 76px; height: 56px; border-radius: 8px; overflow: hidden;
  border: 2px solid transparent; padding: 0; cursor: pointer; background: #ece7de;
}
.pdrawer__thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.pdrawer__thumb--on { border-color: #b52118; }

/* prices */
.pdrawer__prices { display: flex; flex-wrap: wrap; gap: 26px; margin-top: 18px; }
.pdrawer__price { display: flex; flex-direction: column; gap: 1px; }
.pdrawer__price-label { font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: #a39a8c; font-weight: 600; }
.pdrawer__price-value { font-size: 20px; font-weight: 700; color: #1a1814; }

/* performance stat tiles */
.pdrawer__perf { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.pdrawer__perf-tile {
  display: flex; align-items: center; gap: 12px;
  border: 1px solid #ece7de; border-radius: 12px; padding: 14px 16px; background: #fff;
  cursor: pointer; transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}
.pdrawer__perf-tile:hover {
  border-color: #dcd4c6; box-shadow: 0 8px 24px -16px rgba(40, 33, 22, 0.18); transform: translateY(-1px);
}
.pdrawer__label--row { display: flex; align-items: center; justify-content: space-between; }
.pdrawer__perf-hint { font-size: 11px; font-weight: 600; color: #b52118; letter-spacing: 0; text-transform: none; }
.pdrawer__perf-icon {
  flex: none; display: flex; align-items: center; justify-content: center;
  width: 38px; height: 38px; border-radius: 10px; background: #faf8f4; color: #b52118;
}
.pdrawer__perf-num { font-size: 22px; font-weight: 700; color: #1a1814; line-height: 1; }
.pdrawer__perf-lbl { font-size: 12.5px; color: #756f64; margin-top: 3px; }
@media (max-width: 600px) { .pdrawer__perf { grid-template-columns: 1fr; } }

/* sections */
.pdrawer__section { margin-top: 20px; padding-top: 18px; border-top: 1px solid #ece7de; }
.pdrawer__label {
  font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase;
  color: #a39a8c; font-weight: 600; margin-bottom: 12px;
}
.pdrawer__count { color: #6a645a; background: #f1ece3; border-radius: 999px; padding: 1px 7px; letter-spacing: 0; }

.pdrawer__specs { display: flex; flex-wrap: wrap; gap: 12px; }
.pdrawer__spec {
  display: flex; align-items: center; gap: 8px;
  background: #fff; border: 1px solid #ece7de; border-radius: 10px; padding: 10px 14px;
}
.pdrawer__spec .v-icon { color: #756f64; }
.pdrawer__spec-val { font-size: 15px; font-weight: 700; color: #1a1814; }
.pdrawer__spec-label { font-size: 12px; color: #756f64; }

.pdrawer__details { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 24px; }
@media (max-width: 600px) {
  .pdrawer__details { grid-template-columns: 1fr; }
}
.pdrawer__detail {
  display: flex; align-items: baseline; justify-content: space-between; gap: 12px;
  border-bottom: 1px solid #f1ece3; padding-bottom: 7px;
}
.pdrawer__detail-k { font-size: 12.5px; color: #756f64; }
.pdrawer__detail-v { font-size: 13px; font-weight: 600; color: #1a1814; }

.pdrawer__amen { margin-bottom: 13px; }
.pdrawer__amen-name { font-size: 12.5px; font-weight: 600; color: #1a1814; margin-bottom: 6px; }
.pdrawer__amen-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.pdrawer__amen-chip { font-size: 12px; color: #56504a; background: #f1ece3; border-radius: 999px; padding: 4px 11px; }

.pdrawer__blueprint { width: 100%; max-height: 320px; object-fit: contain; border-radius: 12px; background: #fff; border: 1px solid #ece7de; cursor: zoom-in; }

.pdrawer__desc { font-size: 13.5px; line-height: 1.6; color: #56504a; margin: 0; white-space: pre-line; }

.pdrawer__owner { margin-bottom: 6px; }
.pdrawer__owner-name { font-size: 14px; font-weight: 600; color: #1a1814; display: block; }
.pdrawer__owner-mail { font-size: 13px; color: #756f64; text-decoration: none; }
.pdrawer__owner-mail:hover { color: #b52118; }

/* asesores */
.pdrawer__advisors { display: flex; flex-wrap: wrap; gap: 7px; margin-bottom: 12px; }
.pdrawer__advisor {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12.5px; font-weight: 600; color: #1a1814;
  background: #ece7de; border-radius: 999px; padding: 4px 6px 4px 12px;
}
.pdrawer__advisor-x {
  display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px; border: none; border-radius: 50%;
  background: rgba(26, 24, 20, 0.08); color: #6a645a; cursor: pointer;
}
.pdrawer__advisor-x:hover { background: rgba(192, 57, 43, 0.14); color: #c0392b; }
.pdrawer__assign { display: flex; align-items: center; gap: 10px; }
.pdrawer__assign-select { max-width: 280px; }
.pdrawer__assign-select >>> .v-input__slot { border-radius: 10px; }
.pdrawer__assign-select >>> fieldset { border-color: #e7e2d9; }

.pdrawer__obs-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }

/* lightbox */
.pdlight { position: relative; width: 100%; height: 100%; background: rgba(20, 18, 15, 0.94); display: flex; align-items: center; justify-content: center; }
.pdlight img { max-width: 92vw; max-height: 92vh; object-fit: contain; cursor: default; }
.pdlight__close {
  position: absolute; top: 18px; right: 18px; width: 42px; height: 42px;
  border: none; border-radius: 50%; background: rgba(255, 255, 255, 0.14); color: #fff;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
}
.pdlight__nav {
  position: absolute; top: 50%; transform: translateY(-50%); width: 48px; height: 48px;
  border: none; border-radius: 50%; background: rgba(255, 255, 255, 0.14); color: #fff;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
}
.pdlight__nav:hover { background: rgba(255, 255, 255, 0.26); }
.pdlight__nav--prev { left: 20px; }
.pdlight__nav--next { right: 20px; }
</style>
